import {createAction} from '@reduxjs/toolkit'
import {ReservationStatus, VisitorType} from '@wix/events-types'
import {UrlQueryParams, encodeQueryParams} from '@wix/panda-js-utils'
import {
  getEventId,
  isDraft,
  isEventDetailsPageHiddenInternal,
  isExternal,
  isRsvp,
  isTicketed,
  isWithoutRegistration,
} from '@wix/wix-events-commons-statics'
import {ControllerParams, IWixAPI, NavOptions} from '@wix/yoshi-flow-editor'
import {omit} from 'lodash'
import {Store} from 'redux'
import {getSiteSettings, updateSiteSettings} from '../../../../commons/actions/site-settings'
import {DETAILS_ROUTE} from '../../../../commons/constants/navigation'
import {ReservationState} from '../../../../commons/enums'
import {
  getEventListPageRelativeUrl,
  getHomePageUrl,
  hasHeadlessTYP,
  isEditor,
  isHeadless,
  isPreview,
  isSite,
} from '../../../../commons/selectors/environment'
import {isTemplate} from '../../../../commons/selectors/instance'
import {isMember} from '../selectors/current-member-details'
import {getEvent, isRestrictedTo} from '../selectors/event'
import {isMemberRsvpExists} from '../selectors/member-rsvp'
import {getReservationState, getSlug, getUrl, inDetails, inTicketForm, isEventPreview} from '../selectors/navigation'
import {getReservationId} from '../selectors/reservation'
import {shouldNavigateToForm} from '../selectors/rsvp'
import {createAsyncAction} from '../services/redux-toolkit'
import {renderSEOTags, setRobotsNoIndex} from '../services/seo'
import {GetState, RouteParams, State, ThankYouMessageState} from '../types/state'
import {StoreExtraArgs} from '../types/store-extra-arg'
import {parseLocation} from '../utils/navigation'
import {clearCheckout} from './checkout'
import {resetCouponCode} from './coupon'
import {changeEvent} from './event'
import {clearInvoice} from './invoice'
import {closeNativeModal} from './modals'
import {resetPersistentLoadingState} from './pending-requests'
import {getOrder} from './placed-order'
import {getPolicies} from './policies'
import {cancelReservation} from './reservation'

interface NavigateOptions extends NavOptions {
  excludeFromHistory?: boolean
}

// When used it causes full page reload BUT
// editor remembers where it was navigated to
// This is important when modals cause reloads, etc.
export const navigateViaEditor =
  (path: string) =>
  (_dispatch: Function, getState: GetState, {wixCodeApi}: StoreExtraArgs) => {
    wixCodeApi.location.navigateToSection({
      sectionId: 'events',
      state: `${getEvent(getState()).slug}/${path}`,
      queryParams: '',
    })
  }

export const scrollToElement =
  (selector: string) =>
  (_dispatch: Function, _getState: GetState, {wixCodeApi}: StoreExtraArgs) => {
    wixCodeApi.window.scrollToElement?.(selector)
  }

interface NavigateParams {
  route: DETAILS_ROUTE
  routeParams?: RouteParams
  query?: UrlQueryParams
  navOptions?: NavigateOptions
}

export const navigate = createAsyncAction<
  ({route: DETAILS_ROUTE; queryParams: UrlQueryParams} & RouteParams) | undefined,
  NavigateParams
>(
  'NAVIGATE',
  async (
    {route, routeParams = {}, query, navOptions = {disableScrollToTop: true}},
    {getState, dispatch, extra: {wixCodeApi, flowAPI}},
  ) => {
    const state = getState()
    const {route: validRoute, routeParams: validRouteParams} = validateRoute(
      state,
      route,
      routeParams,
      flowAPI.environment.isMobile,
    )

    if (isEditor(state)) {
      const {settings} = await dispatch(getSiteSettings()).unwrap()
      dispatch(updateSiteSettings({...settings.settings, detailsPagePath: route}))
    }

    const queryParams = query || wixCodeApi.location.query
    const validQueryParams =
      validRoute === DETAILS_ROUTE.DETAILS ? omit(queryParams, ['reservationId', 'orderNumber']) : queryParams

    if (isSite(state) || isPreview(state)) {
      const encodedQuery = encodeQueryParams(validQueryParams)
      const {url: nextUrl, external} = getUrl(getState(), validRoute, validRouteParams, encodedQuery, wixCodeApi)
      nextUrl && wixCodeApi.location.to(nextUrl, navOptions)

      if (external) {
        return undefined
      }
    }

    if (isEditor(state) || isPreview(state)) {
      dispatch(resetPersistentLoadingState())
      dispatch(updateNavigationState({route: validRoute, ...validRouteParams}))
    }

    return {route: validRoute, ...validRouteParams, queryParams: validQueryParams}
  },
)

const handleRoute =
  (route: DETAILS_ROUTE, query: UrlQueryParams) =>
  async (
    dispatch: Function,
    getState: GetState,
    {
      wixCodeApi,
      flowAPI: {
        translations: {t},
      },
    }: StoreExtraArgs,
  ) => {
    const state = getState()
    const reservationState = getReservationState(state)

    // @ts-expect-error
    const siteName = wixCodeApi.site.getSiteDisplayName?.()

    const canSetPageTitle = !!siteName

    switch (route) {
      case DETAILS_ROUTE.DETAILS:
        return handleDetailsRoute(state, dispatch, wixCodeApi)
      case DETAILS_ROUTE.FORM:
        if (canSetPageTitle) {
          wixCodeApi.seo.setTitle(t('registrationPage_title', {siteName}))
        }

        return handleFormRoute(wixCodeApi, dispatch)
      case DETAILS_ROUTE.TICKET_FORM:
        if (canSetPageTitle) {
          wixCodeApi.seo.setTitle(t('checkoutPage_title', {siteName}))
        }

        return handleFormRoute(wixCodeApi, dispatch)
      case DETAILS_ROUTE.ORDER:
        const {eventId, orderNumber, token} = query

        if (hasHeadlessTYP(state)) {
          return null
        }

        const response = await dispatch(getOrder({eventId, orderNumber, token}))

        if (canSetPageTitle) {
          if (reservationState === ReservationState.ERROR) {
            wixCodeApi.seo.setTitle(t('paymentErrorPage_title', {siteName}))
          } else if (reservationState === ReservationState.CANCELED) {
            wixCodeApi.seo.setTitle(t('paymentCanceledPage_title', {siteName}))
          } else {
            wixCodeApi.seo.setTitle(t('thankYouPage_title', {siteName}))
          }
        }

        return response
      case DETAILS_ROUTE.THANK_YOU_MESSAGES:
        if (canSetPageTitle) {
          wixCodeApi.seo.setTitle(t('thankYouPage_title', {siteName}))
        }

        return null
      case DETAILS_ROUTE.LINK_EXPIRED:
        if (canSetPageTitle) {
          wixCodeApi.seo.setTitle(t('orderLinkExpiredPage_title', {siteName}))
        }

        return null
      case DETAILS_ROUTE.NOT_FOUND:
        if (canSetPageTitle) {
          wixCodeApi.seo.setTitle(t('orderLinkInvalidPage_title', {siteName}))
        }

        return null
      default:
        return null
    }
  }

const handleFormRoute = (wixCodeApi: IWixAPI, dispatch: Function) => {
  setRobotsNoIndex(wixCodeApi)
  return dispatch(getPolicies({showAlert: false}))
}

const handleDetailsRoute = (state: State, dispatch: Function, wixCodeApi: IWixAPI) => {
  const event = getEvent(state)

  if (isTicketed(event)) {
    const eventId = event.id
    const reservationId = getReservationId(state)

    setTimeout(() => {
      // is case there is pending payment request wait for 1500 to all data to sync between services
      dispatch(cancelReservation({eventId, reservationId}))
    }, 1500)

    dispatch(resetCouponCode())
    dispatch(clearInvoice())
    dispatch(clearCheckout())
    dispatch(removeCheckoutQueryParams())
  }

  return renderSEOTags({wixCodeApi, state})
}

export const validateRoute = (state: State, route: DETAILS_ROUTE, routeParams: RouteParams, mobile: boolean) => {
  const event = getEvent(state)

  if (!event) {
    return {route: DETAILS_ROUTE.NOT_FOUND}
  }

  if (isExternal(event) && route !== DETAILS_ROUTE.FORM) {
    return {route: DETAILS_ROUTE.DETAILS, routeParams}
  }

  switch (route) {
    case DETAILS_ROUTE.DETAILS:
      return {route: validateDetailsRoute(state), routeParams}
    case DETAILS_ROUTE.FORM:
      return {route: validateFormRoute(state), routeParams}
    case DETAILS_ROUTE.TICKET_FORM:
      return {route: validateTicketFormRoute(state, mobile), routeParams}
    case DETAILS_ROUTE.CHANGE_RSVP:
      return {route: validateChangeRsvpRoute(state), routeParams}
    case DETAILS_ROUTE.PICK_TICKETS:
      return {route: validateTicketsRoute(state, mobile), routeParams}
    case DETAILS_ROUTE.CHECKOUT:
      return {route: validateCheckoutRoute(state, mobile), routeParams}
    default:
      return {route, routeParams}
  }
}

const validateDetailsRoute = (state: State) => {
  const event = getEvent(state)
  const detailsPageEnabled = !isEventDetailsPageHiddenInternal(event)

  if (isWithoutRegistration(event) && !detailsPageEnabled) {
    return DETAILS_ROUTE.DETAILS
  }

  return isEditor(state) || detailsPageEnabled ? DETAILS_ROUTE.DETAILS : DETAILS_ROUTE.FORM
}

const validateTicketFormRoute = (state: State, mobile: boolean) => {
  const event = getEvent(state)
  if (!isDraft(event) && !isTemplate(state) && !isEventPreview(state) && isSite(state) && !getReservationId(state)) {
    if (isHeadless(state)) {
      return DETAILS_ROUTE.LINK_EXPIRED
    }
    return mobile ? DETAILS_ROUTE.PICK_TICKETS : DETAILS_ROUTE.DETAILS
  }

  return DETAILS_ROUTE.TICKET_FORM
}

const validateCheckoutRoute = (state: State, mobile: boolean) => validateTicketFormRoute(state, mobile)

const validateFormRoute = (state: State) => {
  const event = getEvent(state)
  const detailsPageEnabled = !isEventDetailsPageHiddenInternal(event)
  const isTicketedEvent = isTicketed(event)

  if (isWithoutRegistration(event) || isExternal(event)) {
    return isEditor(state) ? DETAILS_ROUTE.FORM : DETAILS_ROUTE.DETAILS
  }

  if (isSite(state) && isRestrictedTo(state, VisitorType.MEMBER) && !isMember(state) && detailsPageEnabled) {
    return DETAILS_ROUTE.DETAILS
  }

  if (isTicketedEvent) {
    return isEditor(state) ? DETAILS_ROUTE.TICKET_FORM : DETAILS_ROUTE.DETAILS
  }

  if (isMemberRsvpExists(state) && !isTicketedEvent && !shouldNavigateToForm(state)) {
    return DETAILS_ROUTE.CHANGE_RSVP
  }

  return DETAILS_ROUTE.FORM
}

const validateChangeRsvpRoute = (state: State) =>
  isRsvp(getEvent(state)) && isMember(state) ? DETAILS_ROUTE.CHANGE_RSVP : DETAILS_ROUTE.DETAILS

const validateTicketsRoute = (state: State, mobile: boolean) => {
  if (isEditor(state) && !mobile) {
    // Probably opened desktop editor after playing in mobile preview
    return DETAILS_ROUTE.DETAILS
  }

  return DETAILS_ROUTE.PICK_TICKETS
}

export const navigateToDetails = () => navigate({route: DETAILS_ROUTE.DETAILS})

export const navigateBack = createAction('NAVIGATE_BACK')

export const navigateToForm = (queryParams?: UrlQueryParams) =>
  navigate({route: DETAILS_ROUTE.FORM, routeParams: undefined, query: queryParams})

export const navigateToTicketForm = (reservationId?: string) =>
  navigate({
    route: DETAILS_ROUTE.TICKET_FORM,
    routeParams: undefined,
    query: reservationId ? {reservationId} : undefined,
  })

export const navigateToMobileCheckout = (reservationId?: string) =>
  navigate({
    route: DETAILS_ROUTE.CHECKOUT,
    routeParams: undefined,
    query: reservationId ? {reservationId} : undefined,
  })

export const navigateToTicketsPicker = () => navigate({route: DETAILS_ROUTE.PICK_TICKETS})

export const navigateToChangeRsvp = () => navigate({route: DETAILS_ROUTE.CHANGE_RSVP})

export const navigateToNotFound = () => navigate({route: DETAILS_ROUTE.NOT_FOUND})

export const navigateToLinkExpired = () => navigate({route: DETAILS_ROUTE.LINK_EXPIRED})

export const navigateToThankYouMessage = (thankYouMessageState: ThankYouMessageState) =>
  navigate({route: DETAILS_ROUTE.THANK_YOU_MESSAGES, routeParams: {thankYouMessageState}, query: undefined})

export const navigateToOrder = createAsyncAction<
  void,
  {reservationId: string; reservationState: ReservationState; queryParams: UrlQueryParams}
>('NAVIGATE_TO_ORDER', async ({reservationId, reservationState, queryParams}, {dispatch}) => {
  dispatch(
    navigate({
      route: DETAILS_ROUTE.ORDER,
      routeParams: {reservationState, reservationId},
      query: queryParams,
      navOptions: {excludeFromHistory: true},
    }),
  )
})

export const navigateToOrderUrl = createAsyncAction<void, string>(
  'NAVIGATE_TO_ORDER_URL',
  async (url: string, {getState, dispatch, extra: {wixCodeApi}}) => {
    const baseUrl = wixCodeApi.location.baseUrl
    const relativeUrl = url.replace(baseUrl, '')
    const excludeFromHistory = !isHeadless(getState())

    dispatch(
      navigateTo(excludeFromHistory ? relativeUrl : url, {
        excludeFromHistory,
      }),
    )
  },
)

export const navigateToTemplateOrder = () => navigate({route: DETAILS_ROUTE.TEMPLATE_ORDER})

export const navigateToMainPage =
  () =>
  async (_dispatch: Function, getState: GetState, {wixCodeApi}: StoreExtraArgs) => {
    const {pages} = await wixCodeApi.site.getSiteStructure()
    const homePage = pages.find(page => page.isHomePage) ?? pages[0]
    const url = getEventListPageRelativeUrl(getState()) ?? homePage.url

    wixCodeApi.location.to(url)
  }

export const navigateToHomePage =
  () =>
  async (_dispatch: Function, getState: GetState, {wixCodeApi}: StoreExtraArgs) => {
    const homePageUrl = getHomePageUrl(getState())
    wixCodeApi.location.to(homePageUrl)
  }

export const navigateTo =
  (url: string, navOptions: NavigateOptions = {disableScrollToTop: true, excludeFromHistory: false}) =>
  async (_dispatch: Function, _getState: GetState, {wixCodeApi}: StoreExtraArgs) => {
    wixCodeApi.location.to(url, navOptions)
  }

export const handleInitialNavigation = createAsyncAction(
  'HANDLE_INITIAL_NAVIGATION',
  async (_, {dispatch, extra: {wixCodeApi, flowAPI}}) => {
    const {route, query, ...routeParams} = parseLocation(wixCodeApi)
    const {queryParams: validQueryParams, ...params} = await dispatch(navigate({route, routeParams, query})).unwrap()

    try {
      await dispatch(handleRoute(params.route, validQueryParams))
    } catch (e) {
      console.error(e)
      flowAPI.panoramaClient.errorMonitor().reportError(e)
    }

    return params
  },
)

export const handleCacheRestore = createAsyncAction(
  'HANDLE_CACHE_RESTORE',
  async (_, {getState, dispatch, extra: {serverApi}}) => {
    const state = getState()
    const event = getEvent(state)
    const eventId = getEventId(event)
    const reservationId = getReservationId(state)
    const ticketed = isTicketed(event)

    dispatch(resetPersistentLoadingState())
    dispatch(closeNativeModal())

    if (inDetails(state) && ticketed) {
      setTimeout(() => {
        dispatch(cancelReservation({eventId, reservationId}))
      }, 1500)
    }

    if (inTicketForm(state)) {
      const invoice = await serverApi.getInvoice(eventId, reservationId)

      if (invoice.reservationStatus !== ReservationStatus.RESERVATION_PENDING) {
        dispatch(navigateToDetails())
      }
    }
  },
)

export const updateNavigationState = createAction<{route: DETAILS_ROUTE} & RouteParams>('UPDATE_NAVIGATION_STATE')

export const removeCheckoutQueryParams = createAction('REMOVE_CHECKOUT_QUERY_PARAMS')

export const addLocationListener = (controllerParams: ControllerParams, store: Store) => {
  const {
    controllerConfig: {wixCodeApi},
    flowAPI,
  } = controllerParams
  wixCodeApi.location.onChange(async () => {
    const state = store.getState()
    const event = getEvent(state)
    const {route, ...routeParams} = parseLocation(wixCodeApi)
    const {slug} = routeParams

    const ignoreSlugChange = route === DETAILS_ROUTE.ORDER

    if (slug && slug !== getSlug(state) && !ignoreSlugChange) {
      return store.dispatch<any>(changeEvent(slug))
    }

    if (isSite(state)) {
      store.dispatch(resetPersistentLoadingState())

      const {route: validRoute, routeParams: validRouteParams} = validateRoute(
        state,
        route,
        routeParams,
        flowAPI.environment.isMobile,
      )

      try {
        await store.dispatch<any>(handleRoute(validRoute, wixCodeApi.location.query))
      } catch (e) {
        flowAPI.panoramaClient.errorMonitor().reportError(e)
      }

      return isExternal(event) ? null : store.dispatch(updateNavigationState({route: validRoute, ...validRouteParams}))
    }
  })
}
